<template>
  <Container class="py-8">
    <Card>
      <template #title>
        {{ $t('pages.admin.firmware.bulk.title') }}
      </template>
      <template #content>
        <div class="flex flex-col gap-6">
          <Message severity="warn" :closable="false">
            {{ $t('pages.admin.firmware.bulk.warning') }}
          </Message>

          <FormGroup :label="$t('pages.admin.firmware.bulk.count_label')" :error-text="error">
            <div class="flex gap-4 items-end">
              <InputNumber
                  v-model="count"
                  :min="1"
                  :max="100"
                  showButtons
                  :disabled="loading"
              />
              <Button
                  :label="$t('pages.admin.firmware.bulk.generate_button')"
                  :loading="loading"
                  :disabled="loading || !count"
                  @click="generate"
              />
            </div>
          </FormGroup>

          <div v-if="results.length > 0" class="flex flex-col gap-4">
            <h3 class="text-lg font-semibold">{{ $t('pages.admin.firmware.bulk.results_title') }}</h3>

            <DataTable :value="results" stripedRows>
              <Column field="id" :header="$t('pages.admin.firmware.bulk.column_id')">
                <template #body="{ data }">
                  <router-link
                      :to="{ name: 'admin.firmware.firmware', params: { firmware: data.id } }"
                      class="text-primary-600 hover:text-primary-800 underline"
                  >
                    {{ data.id }}
                  </router-link>
                </template>
              </Column>
              <Column field="signature_bytes" :header="$t('pages.admin.firmware.bulk.column_bytes')" />
              <Column :header="$t('pages.admin.firmware.bulk.column_signature')">
                <template #body="{ data }">
                  <div class="flex items-center gap-2">
                    <span class="font-mono text-xs truncate max-w-xs">{{ data.signature.substring(0, 40) }}...</span>
                    <Button
                        size="small"
                        text
                        severity="secondary"
                        @click="viewSignature(data)"
                    >
                      <template #icon>
                        <IconEye class="w-4 h-4" />
                      </template>
                    </Button>
                    <Button
                        size="small"
                        text
                        severity="secondary"
                        @click="copySignature(data.signature)"
                    >
                      <template #icon>
                        <IconCopy class="w-4 h-4" />
                      </template>
                    </Button>
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </template>
    </Card>

    <Dialog v-model:visible="signatureDialogVisible" modal :header="$t('pages.admin.firmware.bulk.signature_dialog_title')" :style="{ width: '50rem' }">
      <div class="flex flex-col gap-4">
        <div class="text-sm text-surface-600 dark:text-surface-400">
          {{ $t('pages.admin.firmware.bulk.firmware_id') }}: {{ selectedResult?.id }} |
          {{ $t('pages.admin.firmware.bulk.version') }}: {{ selectedResult?.version }} |
          {{ $t('pages.admin.firmware.bulk.bytes') }}: {{ selectedResult?.signature_bytes }}
        </div>
        <div class="font-mono text-xs bg-surface-100 dark:bg-surface-700 p-3 rounded-md break-all max-h-64 overflow-y-auto">
          {{ selectedResult?.signature }}
        </div>
        <div class="flex justify-end">
          <Button @click="copySignature(selectedResult?.signature)" :label="$t('pages.admin.firmware.bulk.copy_button')" severity="secondary" />
        </div>
      </div>
    </Dialog>
  </Container>
</template>

<script>
import Container from "@/Components/Container.vue";
import FormGroup from "@/Components/FormGroup.vue";
import { IconCopy, IconEye } from '@tabler/icons-vue';
import axios from "axios";

export default {
  name: "FirmwareBulkGenerateView",
  components: {
    FormGroup,
    Container,
    IconCopy,
    IconEye,
  },
  data() {
    return {
      count: 1,
      loading: false,
      error: null,
      results: [],
      signatureDialogVisible: false,
      selectedResult: null,
    }
  },
  methods: {
    async generate() {
      this.loading = true;
      this.error = null;
      this.results = [];

      try {
        const response = await axios.post('firmwares/bulk-generate', {
          count: this.count
        });

        this.results = response.data.results;

        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.complete'),
          detail: response.data.message,
          life: 5000
        });
      } catch (err) {
        this.error = err.response?.data?.error || err.message;
        this.$toast.add({
          severity: 'error',
          summary: this.$t('common.error'),
          detail: this.error,
          life: 5000
        });
      } finally {
        this.loading = false;
      }
    },
    viewSignature(result) {
      this.selectedResult = result;
      this.signatureDialogVisible = true;
    },
    async copySignature(signature) {
      if (signature) {
        await navigator.clipboard.writeText(signature);
        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.complete'),
          detail: this.$t('pages.admin.firmware.bulk.signature_copied'),
          life: 3000
        });
      }
    }
  }
}
</script>
